'use client';

import { AIMessage, AIMessageContent } from '@ahafood/ui/components/ai/message';
import {
  AISuggestion,
  AISuggestions,
} from '@ahafood/ui/components/ai/suggestion';
import { Badge } from '@ahafood/ui/components/badge';
import { Image } from '@ahafood/ui/components/image';
import ReactMarkdown from 'react-markdown';
import { parseBotMessages } from './chat-utils';
import { ChatItemList } from './chatItem-list';
import type { ChatMessage as ChatMessageType, ItemList, User } from './types';

interface ChatMessageProps {
  message: ChatMessageType;
  showAllItemsMap: Record<string, boolean>;
  onToggleShowItems: (messageId: string) => void;
  onSuggestionClick: (suggestion: string) => void;
  onItemClick: (item: ItemList) => void;
  user?: User;
  threadId: string;
  isLoading?: boolean;
}

export function ChatMessage({
  message,
  showAllItemsMap,
  onToggleShowItems,
  onSuggestionClick,
  onItemClick,
  user,
  threadId,
  isLoading = false,
}: ChatMessageProps) {
  if (message.role === 'user') {
    return (
      <AIMessage from="user" key={message.id}>
        <AIMessageContent>
          {(message.parts[0] as { type: 'text'; text: string })?.text}
        </AIMessageContent>
        {user?.picture && (
          <div className="bg-neutral-10 size-10 rounded-full overflow-hidden">
            <Image
              src={user.picture}
              alt={user.name ?? 'User'}
              width={40}
              height={40}
              className="size-10 object-cover"
            />
          </div>
        )}
      </AIMessage>
    );
  }

  if (message.role === 'assistant') {
    const parsed = parseBotMessages(message.parts);
    const showAllItems = showAllItemsMap[message.id] || false;

    return (
      <div
        key={message.id}
        className="space-y-2 flex flex-col items-start gap-2"
      >
        {/* Avatar for assistant */}
        <Badge
          variant="secondary"
          className="py-1 flex items-center gap-1 font-bold border bg-white shadow-md"
        >
          <Image
            src="/static/images/new-ai.png"
            alt="AI Bot"
            width={16}
            height={16}
            className="size-4 mr-1 object-contain"
          />
          <span className="bg-gradient-to-r from-blue-50 to-secondary-60 inline-block text-transparent bg-clip-text pr-0.5 font-bold">
            Amori
          </span>
        </Badge>

        <div className="w-full space-y-2">
          {/* Assistant Message */}
          <AIMessage from="assistant">
            {/* <div className="flex gap-2"> */}
            <AIMessageContent className="bg-neutral-10">
              <ReactMarkdown
                components={{
                  p: ({ children }) => {
                    // Handle text with quotes to make them bold
                    if (typeof children === 'string') {
                      const parts = children.split(/("[^"]*")/);
                      return (
                        <p className="mb-2 last:mb-0">
                          {parts.map((part, index) => {
                            if (part.startsWith('"') && part.endsWith('"')) {
                              return (
                                <strong
                                  key={index}
                                  className="font-bold text-neutral-90"
                                >
                                  {part}
                                </strong>
                              );
                            }
                            return part;
                          })}
                        </p>
                      );
                    }
                    return <p className="mb-2 last:mb-0">{children}</p>;
                  },
                  strong: ({ children }) => (
                    <strong className="font-bold text-neutral-90">
                      {children}
                    </strong>
                  ),
                  em: ({ children }) => <em className="italic">{children}</em>,
                  ul: ({ children }) => (
                    <ul className="list-disc list-inside mb-2 space-y-1">
                      {children}
                    </ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="list-decimal list-inside mb-2 space-y-1">
                      {children}
                    </ol>
                  ),
                  li: ({ children }) => <li className="text-sm">{children}</li>,
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-4 border-primary-20 pl-4 py-2 my-2 bg-neutral-5 rounded-r-lg">
                      {children}
                    </blockquote>
                  ),
                  code: ({ children }) => (
                    <code className="bg-neutral-10 px-1.5 py-0.5 rounded text-sm font-mono">
                      {children}
                    </code>
                  ),
                  pre: ({ children }) => (
                    <pre className="bg-neutral-10 p-3 rounded-lg overflow-x-auto my-2">
                      {children}
                    </pre>
                  ),
                  h1: ({ children }) => (
                    <h1 className="text-lg font-bold mb-2">{children}</h1>
                  ),
                  h2: ({ children }) => (
                    <h2 className="text-base font-bold mb-2">{children}</h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="text-sm font-bold mb-1">{children}</h3>
                  ),
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      className="text-primary-50 underline hover:text-primary-60"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {children}
                    </a>
                  ),
                }}
              >
                {parsed.answer
                  .trim()
                  .replace(/\\n/g, '\n')
                  .replace(/\\\\/g, '\\')}
              </ReactMarkdown>
            </AIMessageContent>
            {/* <Image
                src="/static/images/new-ai.png"
                alt="AI Bot"
                width={16}
                height={16}
                className="size-4 mr-1 object-contain"
              />
            </div> */}
          </AIMessage>

          {/* Item List */}
          {parsed.items.length > 0 && (
            <>
              <div className="text-xs text-neutral-50 mb-4">
                Dưới đây là các món phù hợp với bạn.{' '}
                <strong className="text-neutral-60">"Đặt ngay"</strong> để nhận{' '}
                <span className="bg-white text-primary-40 font-bold">
                  FREESHIP
                </span>{' '}
                của <span className="font-bold text-blue-40">Amori</span> nhé!
              </div>
              <ChatItemList
                items={parsed.items}
                showAllItems={showAllItems}
                onToggleShowAll={() => onToggleShowItems(message.id)}
                onItemClick={onItemClick}
                user={user}
                threadId={threadId}
                itemDescriptions={parsed.item_descriptions}
                isStreamingDescriptions={isLoading}
              />
            </>
          )}

          {/* Suggestions */}
          {parsed.suggestions.length > 0 && (
            <AISuggestions className="flex flex-wrap gap-2 w-full items-start">
              {parsed.suggestions.map((suggestion, i) => (
                <AISuggestion
                  key={i}
                  onClick={onSuggestionClick}
                  suggestion={suggestion}
                  disabled={isLoading}
                  className="break-words whitespace-normal text-left size-fit max-w-full inline-block rounded-2xl py-1"
                />
              ))}
            </AISuggestions>
          )}
        </div>
      </div>
    );
  }

  return null;
}
